import { useState } from 'react';
import { ListingPlaceholder } from './placeholder';
import { shortPlaces } from '../lib/format';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ListingCardData } from '../lib/api';
import { haptic } from '../lib/telegram';
import { useToggleFavorite } from '../lib/queries';
import { Icon } from './Icon';
import { errorMessage, useRelativeDate, useToast } from './ui';

export function PriceText({ price, className = '' }: { price: ListingCardData['price']; className?: string }) {
  const { t } = useTranslation();
  if (!price.formatted) return <span className={`price muted ${className}`}>{t('listing.noPrice')}</span>;
  return <span className={`price ${className}`}>{price.formatted}</span>;
}

export function FavoriteButton({ id, value, compact, overlay }: { id: string; value: boolean; compact?: boolean; overlay?: boolean }) {
  const { t } = useTranslation();
  const toggle = useToggleFavorite();
  const toast = useToast();
  // Оптимистичное отображение: состояние меняется сразу, при ошибке — возвращается и показывается сообщение.
  const shown = toggle.isPending ? !value : value;
  return (
    <button
      type="button"
      className={`fav-btn ${shown ? 'fav-on' : ''} ${compact ? 'fav-compact' : ''} ${overlay ? 'gallery-btn' : ''}`}
      aria-pressed={shown}
      aria-label={shown ? t('favorites.remove') : t('favorites.add')}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        haptic.tap();
        toggle.mutate(
          { id, value: !value },
          { onError: (err) => toast(errorMessage(t, err), 'error') },
        );
      }}
    >
      <Icon name="heart" size={compact ? 18 : overlay ? 20 : 22} />
    </button>
  );
}

export function ListingCard({ item }: { item: ListingCardData }) {
  const { t } = useTranslation();
  const rel = useRelativeDate();
  const unavailable = item.available === false;
  // Фото не загрузилось (сеть, удалено) — вместо пустого квадрата фирменная заглушка.
  const [broken, setBroken] = useState(false);
  return (
    <Link to={`/listing/${item.id}`} className={`card ${item.isHighlighted ? 'card-highlight' : ''} ${unavailable ? 'card-unavailable' : ''}`}>
      <div className="card-media">
        {item.thumbUrl && !broken ? (
          <img src={item.thumbUrl} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
        ) : (
          <ListingPlaceholder categoryId={item.categoryId} />
        )}
        {item.isVip ? <span className="badge badge-vip">VIP</span> : null}
        {item.imageCount > 1 ? <span className="badge badge-count">{item.imageCount}</span> : null}
        {item.isFavorite !== undefined ? <FavoriteButton id={item.id} value={item.isFavorite} compact /> : null}
      </div>
      <div className="card-body">
        <PriceText price={item.price} />
        <div className="card-title">{item.title}</div>
        <div className="card-meta">
          {unavailable ? <span className="status-pill status-archived">{t('favorites.unavailable')}</span> : null}
          {item.location ? <span>{shortPlaces(item.location)}</span> : null}
          {item.publishedAt ? <span>{rel(item.publishedAt)}</span> : null}
        </div>
      </div>
    </Link>
  );
}

export function ListingGrid({ items }: { items: ListingCardData[] }) {
  return (
    <div className="grid">
      {items.map((i) => (
        <ListingCard key={i.id} item={i} />
      ))}
    </div>
  );
}

export function ListingGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="card card-skeleton">
          <div className="card-media skeleton" />
          <div className="card-body">
            <div className="skeleton skeleton-line" style={{ width: '55%' }} />
            <div className="skeleton skeleton-line" />
            <div className="skeleton skeleton-line" style={{ width: '40%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Строка «моего» объявления со статусом. */
export function MyListingRow({ item, onClick }: { item: ListingCardData; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="my-row" onClick={onClick}>
      <div className="my-row-thumb">
        {item.thumbUrl ? <img src={item.thumbUrl} alt="" loading="lazy" /> : <Icon name="camera" size={20} />}
      </div>
      <div className="my-row-body">
        <div className="card-title">{item.title || t('sell.untitled')}</div>
        <PriceText price={item.price} />
        <span className="row-wrap my-row-pills">
          <span className={`status-pill status-${item.status}`}>{t(`status.${item.status}`)}</span>
          {item.isVip ? <span className="status-pill pill-vip">VIP</span> : null}
          {item.isHighlighted ? <span className="status-pill pill-vip">{t('promote.highlighted')}</span> : null}
        </span>
        {item.status === 'rejected' && item.rejection ? (
          <div className="tiny danger-text">
            {t(`rejectReasons.${item.rejection.code ?? 'other'}`)}
            {item.rejection.text ? ` — ${item.rejection.text}` : ''}
          </div>
        ) : null}
      </div>
      <Icon name="chevron" size={18} className="muted" />
    </button>
  );
}
